import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../lib/auth";

/**
 * Lightweight feedback widget. Floats bottom-right on every page inside the
 * app shell. Clicking opens a glass dialog with a textarea; submitting builds
 * a mailto: link with the user's email, the current URL, and the message
 * pre-filled — no backend endpoint required yet.
 *
 * Visual: cyan-accent FAB with the accent glow on hover. Dialog uses the
 * .glass treatment over a backdrop-blurred scrim so the page stays visible
 * but recedes.
 */
const FEEDBACK_TO = "kane@yesandeverything.com";

export function FeedbackButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  // Esc closes the dialog.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const subject = encodeURIComponent("Scheduler feedback");
    const lines = [
      message.trim(),
      "",
      "---",
      `From: ${user?.display_name ?? "(unknown)"} <${user?.email ?? "(no email)"}>`,
      `URL: ${window.location.href}`,
      `User agent: ${navigator.userAgent}`,
    ];
    const body = encodeURIComponent(lines.join("\n"));
    window.location.href = `mailto:${FEEDBACK_TO}?subject=${subject}&body=${body}`;
    setOpen(false);
    setMessage("");
  }

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-1.5
                   rounded-full bg-gradient-to-br from-accent to-accent-press
                   px-4 py-2.5 text-sm font-medium text-ink-0
                   shadow-elev3 ring-1 ring-inset ring-white/15
                   transition-all duration-300 ease-out
                   hover:shadow-glow-accent hover:-translate-y-0.5
                   active:translate-y-0 active:scale-[0.97]
                   sm:bottom-6 sm:right-6"
        aria-label="Send feedback"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Feedback
      </button>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-title"
          className="fixed inset-0 z-50 grid place-items-center
                     bg-ink-0/70 backdrop-blur-sm px-4 py-6
                     motion-safe:animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md rounded-xl glass p-5 sm:p-6
                       motion-safe:animate-toast-in"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2
                  id="feedback-title"
                  className="text-base font-semibold text-fg-0"
                >
                  Send feedback
                </h2>
                <p className="mt-1 text-xs text-fg-2">
                  Goes straight to Nick. Bug reports, feature requests, anything
                  confusing — all welcome.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="grid h-8 w-8 place-items-center rounded-md text-fg-2
                           transition-colors hover:bg-ink-2 hover:text-fg-0"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <label
              htmlFor="feedback-message"
              className="mt-4 block text-sm font-medium text-fg-1"
            >
              Your message
            </label>
            <textarea
              id="feedback-message"
              required
              autoFocus
              rows={5}
              maxLength={2000}
              placeholder="What's working, what's not, what would help..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="field mt-1.5 resize-y"
            />
            <p className="mt-1.5 text-xs text-fg-2">
              We'll include the page you're on and your account info
              automatically.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                Send
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </>
  );
}
